import React, { FC, useCallback } from 'react';
import memoize from 'memoizee';
import { useDebounceCallback } from 'usehooks-ts';

import { OrganizationDTO, useAutosuggestionOrganizationsHook } from '@/api/generated';
import DropdownSelect, { DropdownSelectOption } from '@/components/fields/DropdownSelect';

const DEBOUNCE_DELAY = 300;

type OrganizationDropdownSelectProps = Omit<React.ComponentProps<typeof DropdownSelect>, 'options'>;

const filterByIds = (options: DropdownSelectOption[], ids?: string[]) => {
  if (!ids) return [];

  const idsSet = new Set(ids);

  return options.filter(option => idsSet.has(option.value));
};

const OrganizationDropdownSelect: FC<OrganizationDropdownSelectProps> = props => {
  const getOrganizationsBySearch = useAutosuggestionOrganizationsHook();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadOrganizationOptions = useCallback(
    memoize(
      async (inputValue?: string): Promise<DropdownSelectOption[]> => {
        const organizations = await getOrganizationsBySearch({ search: inputValue ?? '' });

        const newOrganizationOptions: DropdownSelectOption[] = (organizations || []).map(
          (organization: OrganizationDTO) => ({
            label: organization.name!,
            value: organization.slug!
          })
        );

        return newOrganizationOptions;
      },
      { promise: true }
    ),
    []
  );

  const loadOrganizationOptionsDebounced = useDebounceCallback(loadOrganizationOptions, DEBOUNCE_DELAY, {
    leading: true
  });

  const loadOrganizationOptionsByIds = useCallback(
    async (slugs?: string[]) => {
      const newOrganizationOptions = await loadOrganizationOptions();

      return filterByIds(newOrganizationOptions!, slugs);
    },
    [loadOrganizationOptions]
  );

  return (
    <DropdownSelect
      {...props}
      loadOptions={loadOrganizationOptionsDebounced}
      loadSelectedOptions={loadOrganizationOptionsByIds}
    />
  );
};

export default OrganizationDropdownSelect;
